import * as React from "react";
import { Check, ChevronDown, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

export type Option = { label: string; value: string };

interface MultiSelectProps {
  options?: (string | Option)[];
  selected?: string[];
  onChange?: (vals: string[]) => void;
  placeholder?: string;
  className?: string;
  multiple?: boolean;
  clearable?: boolean;
  disabled?: boolean;
  "data-testid"?: string;
}

/**
 * Searchable select component supporting single and multiple selection.
 * Options can be strings or objects { label, value }.
 */
export function MultiSelect({
  options = [],
  selected = [],
  onChange,
  placeholder = "Selecione...",
  className,
  multiple = true,
  clearable = true,
  disabled = false,
  "data-testid": testId,
}: MultiSelectProps) {
  const [open, setOpen] = React.useState(false);

  // Normalize options to { label, value }
  const normalizedOptions = React.useMemo(() => {
    return options.map(opt => {
      if (typeof opt === "string") return { label: opt, value: opt };
      return opt;
    });
  }, [options]);

  const handleUnselect = (value: string) => {
    onChange?.(selected.filter((v) => v !== value));
  };

  const handleSelect = (value: string) => {
    if (!onChange) return;
    if (multiple) {
      const isSelected = selected.includes(value);
      onChange(
        isSelected
          ? selected.filter((v) => v !== value)
          : [...selected, value]
      );
    } else {
      onChange([value]);
      setOpen(false);
    }
  };

  const selectedLabels = React.useMemo(() => {
    return selected.map((val: string) => {
      const opt = normalizedOptions.find((o: Option) => o.value === val);
      return opt ? opt.label : val;
    });
  }, [selected, normalizedOptions]);

  const displayLabel = React.useMemo(() => {
    if (selected.length === 0) return placeholder;
    if (multiple) return null;
    return selectedLabels[0];
  }, [selected.length, multiple, placeholder, selectedLabels]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          data-testid={testId as string | undefined}
          className={cn(
            "w-full justify-between h-auto min-h-[48px] px-3 py-2 bg-cream-50 border-cream-300 hover:border-heat-400 hover:bg-cream-50 transition-all text-left",
            className
          )}
        >
          <div className="flex flex-wrap gap-1.5 overflow-hidden">
            {multiple && selected.length > 0 ? (
              selected.map((val, idx) => {
                const label = selectedLabels[idx];
                return (
                  <Badge
                    variant="secondary"
                    key={val}
                    className="bg-ink-900 text-cream-50 border-ink-800 hover:bg-ink-800 transition-colors gap-1 pr-1"
                    onClick={(e: React.MouseEvent) => {
                      e.stopPropagation();
                      handleUnselect(val);
                    }}
                  >
                    <span className="truncate max-w-[120px]">{label}</span>
                    <div
                      className="ml-1 ring-offset-background rounded-full outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          handleUnselect(val);
                        }
                      }}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                      }}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleUnselect(val);
                      }}
                    >
                      <X className="h-3 w-3 text-heat-400 hover:text-heat-600" />
                    </div>
                  </Badge>
                );
              })
            ) : (
              <span className={cn(
                "text-sm font-semibold truncate",
                selected.length > 0 ? "text-ink-900" : "text-ink-400 font-normal"
              )}>
                {displayLabel}
              </span>
            )}
          </div>
          <div className="flex items-center gap-1">
            {clearable && selected.length > 0 && (
              <div
                role="button"
                className="p-1 hover:bg-cream-200 rounded-full transition-colors"
                onClick={(e) => {
                  e.stopPropagation();
                  onChange?.([]);
                }}
              >
                <X className="h-3.5 w-3.5 text-ink-400 hover:text-ink-600" />
              </div>
            )}
            <ChevronDown className="h-4 w-4 shrink-0 opacity-50 text-ink-500" />
          </div>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto min-w-[--radix-popover-trigger-width] max-w-[400px] p-0" align="start">
        <Command className="bg-cream-50 border border-cream-200 shadow-xl">
          <CommandInput placeholder="Buscar..." className="h-11 border-b border-cream-100 bg-transparent text-ink-900" />
          <CommandList className="max-h-72">
            <CommandEmpty className="py-6 text-center text-sm text-ink-400">Nenhum resultado.</CommandEmpty>
            <CommandGroup className="p-1.5">
              {normalizedOptions.map((option) => {
                const isSelected = selected.includes(option.value);
                return (
                  <CommandItem
                    key={option.value}
                    onSelect={() => handleSelect(option.value)}
                    className={cn(
                      "cursor-pointer rounded-md px-2.5 py-2 mb-0.5 transition-colors flex items-center justify-between",
                      "hover:bg-heat-50/50 aria-selected:bg-heat-50",
                      "dark:hover:bg-heat-900/20 dark:aria-selected:bg-heat-900/40"
                    )}
                  >
                    <span className={cn(
                      "text-sm font-medium transition-colors whitespace-nowrap",
                      isSelected ? "text-heat-700 dark:text-heat-400" : "text-ink-900 dark:text-ink-200"
                    )}>
                      {option.label}
                    </span>
                    <Check
                      className={cn(
                        "h-4 w-4 ml-2 shrink-0 text-heat-600 transition-opacity",
                        isSelected ? "opacity-100" : "opacity-0"
                      )}
                    />
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}